import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { apiService } from '../services/apiService';
import { Organization, UserOrgMembership } from '../types';
import { useAuth } from './AuthContext';
import { DEMO_ORGANIZATION, isDemoOrg } from '../data/demoData';
import { analyticsService } from '../services/analyticsService';

interface OrgContextType {
  organizations: Organization[];
  memberships: UserOrgMembership[];
  currentOrganization: Organization | null;
  hasOrganizations: boolean;
  maxOrganizations: number;
  canCreateMore: boolean;
  loading: boolean;
  error: string | null;
  checkUserMembership: () => Promise<boolean>;
  createOrganization: (name: string, urlName: string) => Promise<Organization | null>;
  getUserOrganizations: () => Promise<void>;
  selectOrganization: (urlName: string) => Promise<Organization | null>;
  setCurrentOrganizationDirectly: (org: Organization) => void;
  checkUrlAvailability: (urlName: string) => Promise<boolean>;
  refreshOrganizations: () => Promise<void>;
  updateOrganization: (urlName: string, updates: { name?: string }) => Promise<boolean>;
  deleteOrganization: (urlName: string) => Promise<boolean>;
  isCurrentUserOwner: (organization: Organization) => boolean;
  testAuth: () => Promise<void>;
  // Member management functions (admin only)
  addMemberToOrganization: (urlName: string, email: string, role?: string) => Promise<boolean>;
  getOrganizationMembers: (urlName: string) => Promise<UserOrgMembership[] | null>;
  removeMemberFromOrganization: (urlName: string, memberEmail: string) => Promise<boolean>;
}

const OrgContext = createContext<OrgContextType | undefined>(undefined);

export const useOrg = () => {
  const context = useContext(OrgContext);
  if (context === undefined) {
    throw new Error('useOrg must be used within an OrgProvider');
  }
  return context;
};

interface OrgProviderProps {
  children: ReactNode;
}

export const OrgProvider: React.FC<OrgProviderProps> = ({ children }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [memberships, setMemberships] = useState<UserOrgMembership[]>([]);
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [hasOrganizations, setHasOrganizations] = useState<boolean>(false);
  const [maxOrganizations, setMaxOrganizations] = useState<number>(1);
  const [canCreateMore, setCanCreateMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const { currentUser } = useAuth();

  // Check if user has any organization memberships
  const checkUserMembership = React.useCallback(async (): Promise<boolean> => {
    if (!currentUser) {
      setHasOrganizations(false);
      setMaxOrganizations(1);
      setCanCreateMore(true);
      return false;
    }

    try {
      setError(null);
      const response = await apiService.checkUserMembership();
      
      if (response.error) {
        setError(response.error);
        return false;
      }

      const hasOrgs = response.data?.hasOrganizations || false;
      const maxOrgs = response.data?.maxOrganizations || 1;
      const canCreate = response.data?.canCreateMore || false;
      
      setHasOrganizations(hasOrgs);
      setMaxOrganizations(maxOrgs);
      setCanCreateMore(canCreate);
      return hasOrgs;
    } catch (error) {
      console.error('Failed to check user membership:', error);
      setError('Failed to check organization membership');
      return false;
    }
  }, [currentUser]);

  // Get user's organizations
  const getUserOrganizations = React.useCallback(async (): Promise<void> => {
    if (!currentUser) {
      setOrganizations([]);
      setMemberships([]);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.getUserOrganizations();
      
      if (response.error) {
        setError(response.error);
        return;
      }

      if (response.data) {
        setOrganizations(response.data.organizations);
        setMemberships(response.data.memberships);
        setHasOrganizations(response.data.count > 0);

        // Auto-select first organization if none is selected
        if (response.data.organizations.length > 0 && !currentOrganization) {
          const firstOrg = response.data.organizations[0];
          setCurrentOrganization(firstOrg);
          apiService.setCurrentOrganization(firstOrg);
        }
      }
    } catch (error) {
      console.error('Failed to get user organizations:', error);
      setError('Failed to load organizations');
    } finally {
      setLoading(false);
    }
  }, [currentUser, currentOrganization]);

  // Create a new organization
  const createOrganization = async (name: string, urlName: string): Promise<Organization | null> => {
    if (!currentUser) {
      setError('Authentication required');
      return null;
    }

    try {
      setError(null);
      setLoading(true);

      const response = await apiService.createOrganization({ name, urlName });
      
      if (response.error) {
        setError(response.error);
        analyticsService.trackError('org_creation_error', response.error);
        return null;
      }

      if (response.data) {
        analyticsService.trackOrganizationCreated(name, urlName);
        const newOrg = response.data.organization;
        const newMembership = response.data.membership;
        
        // Add to local state
        setOrganizations(prev => [newOrg, ...prev]);
        setMemberships(prev => [newMembership, ...prev]);
        setHasOrganizations(true);
        
        // Auto-select the new organization
        setCurrentOrganization(newOrg);
        apiService.setCurrentOrganization(newOrg);
        
        return newOrg;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to create organization:', error);
      setError('Failed to create organization');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Track failed organization lookups to prevent infinite loops
  const [failedLookups, setFailedLookups] = React.useState<Set<string>>(new Set());

  // Select an organization by URL name
  const selectOrganization = async (urlName: string): Promise<Organization | null> => {
    // Prevent repeated failed attempts
    if (failedLookups.has(urlName)) {
      return null;
    }

    try {
      setError(null);
      
      // First check if we already have it locally
      const existingOrg = organizations.find(org => org.urlName === urlName);
      if (existingOrg) {
        setCurrentOrganization(existingOrg);
        apiService.setCurrentOrganization(existingOrg);
        // Remove from failed lookups if it exists now
        setFailedLookups(prev => {
          const next = new Set(prev);
          next.delete(urlName);
          return next;
        });
        return existingOrg;
      }

      // Check if this is a demo organization - handle locally without API call
      if (isDemoOrg(urlName)) {
        const demoOrg = DEMO_ORGANIZATION;
        setCurrentOrganization(demoOrg);
        apiService.setCurrentOrganization(demoOrg);
        analyticsService.trackDemoModeActivated();
        analyticsService.trackOrganizationSelected(demoOrg.name, demoOrg.urlName);
        return demoOrg;
      }

      // Fetch from API for non-demo organizations
      const response = await apiService.getOrganizationByUrlName(urlName);
      
      if (response.error) {
        console.error(`Failed to load organization ${urlName}:`, response.error);
        setError(response.error);
        analyticsService.trackError('org_selection_error', response.error);
        
        // Add to failed lookups to prevent repeated attempts
        setFailedLookups(prev => new Set(prev).add(urlName));
        return null;
      }

      if (response.data) {
        const org = response.data.organization;
        setCurrentOrganization(org);
        apiService.setCurrentOrganization(org);
        analyticsService.trackOrganizationSelected(org.name, org.urlName);
        // Remove from failed lookups on success
        setFailedLookups(prev => {
          const next = new Set(prev);
          next.delete(urlName);
          return next;
        });
        return org;
      }
      
      return null;
    } catch (error) {
      console.error('Failed to select organization:', error);
      setError('Failed to load organization');
      
      // Add to failed lookups to prevent repeated attempts
      setFailedLookups(prev => new Set(prev).add(urlName));
      return null;
    }
  };

  // Check if URL name is available
  const checkUrlAvailability = async (urlName: string): Promise<boolean> => {
    try {
      const response = await apiService.checkUrlAvailability(urlName);
      return response.data?.available || false;
    } catch (error) {
      console.error('Failed to check URL availability:', error);
      return false;
    }
  };

  // Refresh organizations
  const refreshOrganizations = async (): Promise<void> => {
    await getUserOrganizations();
  };

  // Update organization
  const updateOrganization = async (urlName: string, updates: { name?: string }): Promise<boolean> => {
    if (!currentUser) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);
      setLoading(true);

      const response = await apiService.updateOrganization(urlName, updates);

      if (response.error) {
        setError(response.error);
        return false;
      }

      if (response.data) {
        const updatedOrg = response.data.organization;

        // Update in local state
        setOrganizations(prev =>
          prev.map(org => org.urlName === urlName ? updatedOrg : org)
        );

        // Update current organization if it's the one being updated
        if (currentOrganization?.urlName === urlName) {
          setCurrentOrganization(updatedOrg);
        }

        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error('Failed to update organization:', error);
      setError('Failed to update organization');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Delete organization
  const deleteOrganization = async (urlName: string): Promise<boolean> => {
    if (!currentUser) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);
      setLoading(true);

      const response = await apiService.deleteOrganization(urlName);

      if (response.error) {
        setError(response.error);
        return false;
      }

      if (response.success) {
        // Remove from local state immediately since it's marked as deleted
        setOrganizations(prev => prev.filter(org => org.urlName !== urlName));

        setMemberships(prev => {
          const orgToDelete = organizations.find(org => org.urlName === urlName);
          if (orgToDelete) {
            return prev.filter(membership => membership.orgId !== orgToDelete.id);
          }
          return prev;
        });

        // Clear current organization if it was the deleted one
        if (currentOrganization?.urlName === urlName) {
          setCurrentOrganization(null);
        }

        // Update hasOrganizations flag
        const remainingOrgs = organizations.filter(org => org.urlName !== urlName);
        setHasOrganizations(remainingOrgs.length > 0);

        return true;
      } else {
        setError('Failed to delete organization');
        return false;
      }
    } catch (error) {
      console.error('Failed to delete organization:', error);
      setError('Failed to delete organization');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is the owner of an organization
  const isCurrentUserOwner = (organization: Organization): boolean => {
    return currentUser?.uid === organization.createdBy;
  };

  // Test Firebase authentication and API connectivity
  const testAuth = async (): Promise<void> => {
    try {
      await apiService.testFirebaseAuth();
      await apiService.debugAuth();
      await apiService.checkUserMembership();
    } catch (error) {
      console.error('Auth test failed:', error);
    }
  };

  // Member management functions (admin only)
  const addMemberToOrganization = async (urlName: string, email: string, role: string = 'member'): Promise<boolean> => {
    if (!currentUser) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);
      const response = await apiService.addMemberToOrganization(urlName, email, role);
      
      if (response.error) {
        setError(response.error);
        return false;
      }

      // Refresh organizations to get updated member counts
      await refreshOrganizations();
      return true;
    } catch (error) {
      console.error('Failed to add member to organization:', error);
      setError('Failed to add member to organization');
      return false;
    }
  };

  const getOrganizationMembers = async (urlName: string): Promise<UserOrgMembership[] | null> => {
    if (!currentUser) {
      setError('Authentication required');
      return null;
    }

    try {
      setError(null);
      const response = await apiService.getOrganizationMembers(urlName);
      
      if (response.error) {
        setError(response.error);
        return null;
      }

      return response.data?.members || null;
    } catch (error) {
      console.error('Failed to get organization members:', error);
      setError('Failed to get organization members');
      return null;
    }
  };

  const removeMemberFromOrganization = async (urlName: string, memberEmail: string): Promise<boolean> => {
    if (!currentUser) {
      setError('Authentication required');
      return false;
    }

    try {
      setError(null);
      const response = await apiService.removeMemberFromOrganization(urlName, memberEmail);
      
      if (response.error) {
        setError(response.error);
        return false;
      }

      // Refresh organizations to get updated member counts
      await refreshOrganizations();
      return true;
    } catch (error) {
      console.error('Failed to remove member from organization:', error);
      setError('Failed to remove member from organization');
      return false;
    }
  };

  // Set current organization directly (for demo org)
  const setCurrentOrganizationDirectly = (org: Organization) => {
    setCurrentOrganization(org);
    apiService.setCurrentOrganization(org);
    // Also add to organizations list if not already there
    if (!organizations.find(o => o.urlName === org.urlName)) {
      setOrganizations(prev => [...prev, org]);
    }
  };

  // Note: Debounced functions removed in favor of parallel API calls for better performance

  // Initialize on user change with parallel API calls
  useEffect(() => {
    if (currentUser) {
      // Clear any demo organization when user is authenticated
      if (currentOrganization && isDemoOrg(currentOrganization.urlName)) {
        setCurrentOrganization(null);
        apiService.setCurrentOrganization(null);
      }
      
      // Make API calls in parallel for faster loading
      Promise.all([
        checkUserMembership(),
        getUserOrganizations()
      ]).then(() => {
        setLoading(false);
      }).catch((error) => {
        console.error('Failed to initialize user data:', error);
        setLoading(false);
      });
    } else {
      // Reset state when user logs out
      setOrganizations([]);
      setMemberships([]);
      setCurrentOrganization(null);
      apiService.setCurrentOrganization(null);
      setHasOrganizations(false);
      setLoading(false);
      setError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Set demo organization as default when no organization is selected
  useEffect(() => {
    if (!currentUser && !currentOrganization) {
      const demoOrg = {
        id: 1,
        name: 'BeanBuzz Demo',
        urlName: 'beanbuzz-demo',
        createdBy: 'demo@beanbuzz.com',
        createdTime: new Date().toISOString(),
        deleted: false
      };
      setCurrentOrganizationDirectly(demoOrg);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, currentOrganization]);

  const value: OrgContextType = {
    organizations,
    memberships,
    currentOrganization,
    hasOrganizations,
    maxOrganizations,
    canCreateMore,
    loading,
    error,
    checkUserMembership,
    createOrganization,
    getUserOrganizations,
    selectOrganization,
    setCurrentOrganizationDirectly,
    checkUrlAvailability,
    refreshOrganizations,
    updateOrganization,
    deleteOrganization,
    isCurrentUserOwner,
    testAuth,
    addMemberToOrganization,
    getOrganizationMembers,
    removeMemberFromOrganization,
  };

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}; 